import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { ModulesWithAvailableActivityList } from 'src/app/business/available-activities/available-activities.component';
import { hasPermission, InternalSettingsService } from 'src/app/common/services/internal-settings/internal-settings.service';
import { UIUtilityService } from 'src/app/system/utility/ui-utility.service';
import { ExternalSettings } from '../../services/external-settings/external-settings.interface';
import { ExternalSettingsService } from '../../services/external-settings/external-settings.service';
import { PermittedActivityService } from '../../services/permitted-activity/permitted-activity.service';
import { ResponsiveDesignService } from '../../services/responsive-design/responsive-design.service';
import { UserService } from '../../services/user/user.service';
import { PAGroup, PAMenuMobile } from '../../services/permitted-activity/pa-menu-meta';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { MeConfirmDialogService } from '../me-dialog/me-confirm/me-confirm.service';
import { ConfirmDialogConfig, DialogType } from '../me-dialog/me-confirm/me-confirm.component';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { StringUtilityService } from '../../../system/utility/string-utility.service';
import { InternalSettings, PermissionList } from '../../services/internal-settings/internal-settings.interface';
import { HelpDeskService } from 'src/app/business/help-desk/help-desk.service';
import { SupportCenterService } from 'src/app/business/support-center/support-center.service';
import { NavigationService } from 'src/app/system/utility/navigation.service';

@Component({
    selector: 'app-sidebar-mobile',
    templateUrl: './sidebar-mobile.component.html',
    styleUrls: ['./sidebar-mobile.component.less'],
    standalone: false
})
export class SidebarMobileComponent implements OnInit, OnDestroy {

  private destroy$: Subject<void> = new Subject();

  PAMenuMobile: any = PAMenuMobile;
  UIUtilityService: any = UIUtilityService;
  PlatformService: any = PlatformService;
  StringUtilityService: any = StringUtilityService;

  protected internalSettings!: InternalSettings;
  protected externalSettings!: ExternalSettings;
  protected menu!: PAMenuMobile;
  protected isShowingSidebarNav!: boolean;
  protected isVoiceCommandEnabled: boolean = false;
  public hasHelpDesk: boolean = false;

  constructor(
    private translate: TranslateUtilityService,
    private router: Router,
    private srvUser: UserService,
    private srvES: ExternalSettingsService,
    private srvIS: InternalSettingsService,
    private srvPermitted: PermittedActivityService,
    private srvRpDesign: ResponsiveDesignService,
    private srvConfirmDialog: MeConfirmDialogService,
    private srvHelpDesk: HelpDeskService,
    private srvSupport: SupportCenterService,
  ) { }

  ngOnInit(): void {

    combineLatest([
      this.srvES.externalSettings$,
      this.srvIS.getCurrentSettings(),
      this.srvPermitted.menuMobileApp$,
      this.srvPermitted.menuMobileView$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([externalSettings, internalSettings, menuMobileApp, menuMobileView]) => {

        if (!internalSettings) return;

        this.internalSettings = internalSettings;
        this.externalSettings = externalSettings;
        this.menu = PlatformService.isMobile() ? menuMobileApp! : menuMobileView!;

        this.isVoiceCommandEnabled = this.externalSettings.globalVariables.isModuleEnabledVoiceCommand && hasPermission(PermissionList.MOBILEARTH_ACCESS_VOICE_COMMAND, this.internalSettings.permissions);

      });

    this.srvRpDesign.getSidebarNav().subscribe((isShowingSidebarNav: boolean) => {
      this.isShowingSidebarNav = isShowingSidebarNav;
    })
    this.srvSupport.showHelpDesk$.subscribe((show: boolean)=>{
      this.hasHelpDesk = show;
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  protected sidebarDismiss(): void {
    this.srvRpDesign.setSidebarNav(false);
  }

  protected sidebarDismissNav(group: PAGroup): void {
    NavigationService.goto(this.router, group.path, group.params ? { queryParams: { module: group.params } } : {});
    this.sidebarDismiss();
  }

  protected onSettingClick() {
    this.sidebarDismiss();
    this.router.navigate(['/available-activities'], { queryParams: { module: ModulesWithAvailableActivityList.SETTINGS } });
  }

  protected onHelpCenterClick() {
    this.sidebarDismiss();
    this.router.navigate(['/available-activities'], { queryParams: { module: ModulesWithAvailableActivityList.HELP_CENTER } });
  }

  protected onVoiceCommandClick() {
    this.sidebarDismiss();
    this.router.navigateByUrl("/voice-command");
  }

  protected onLogoffClick() {

    // Dismiss Sidebar before showing the dialog
    this.sidebarDismiss();

    let confirmDialogConfig: ConfirmDialogConfig = {
      dialogType: DialogType.NORMAL,
      title: this.translate.instant('SIGN_OUT.DIALOG_HEADER'),
      message: this.translate.instant('SIGN_OUT.DIALOG_MESSAGE'),
      confirmButtonText: this.translate.instant('SIGN_OUT.DIALOG_YES'),
      cancelButtonText: this.translate.instant('SIGN_OUT.DIALOG_NO')
    }

    this.srvConfirmDialog.showDialog(confirmDialogConfig).subscribe((isConfirm) => {
      if (isConfirm) {
        if (this.hasHelpDesk) {
          this.srvHelpDesk.closeTicket(true).subscribe(()=>{
            this.srvUser.logoff();
          });
        } else {
          this.srvUser.logoff();
        }
      }
    });
  }
}